import classNames                    from 'classnames';
import * as React                    from 'react';
import { useTranslation }            from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import DV                            from 'ts-data-validator';

import './styles.scss';

import { useRouter }        from '../../../common/hooks/useRouter';
import { addFileExtension } from '../../../common/tools/getFileExtension';

import { useCatalog } from '../../providers/catalog';
import useWhereBuy    from '../../providers/whereBuy/whereBuy.hook';

import Dropdown        from '../fields/dropdown';
import EyeIcon         from '../icons/EyeIcon';
import LoupeIcon       from '../icons/LoupeIcon';
import SearchCrossIcon from '../icons/SearchCrossIcon';

const TradeStores = function(props) {
  const history = useHistory();
  const match   = useRouteMatch();
  const { t }   = useTranslation();

  const {
          ALT,
          IMAGE,
          LINK,
        } = props;

  const catalog  = useCatalog();
  const whereBuy = useWhereBuy();

  const searchInputRef        = React.useRef(null);
  const storeResultWrapperRef = React.useRef(null);

  const [currentCity, setCurrentCity]                 = React.useState();
  const [currentRegion, setCurrentRegion]             = React.useState();
  const [isSearchOpen, setIsSearchOpen]               = React.useState(false);
  const [searchQuery, setSearchQuery]                 = React.useState('');
  const [selectedCities, setSelectedCities]           = React.useState([]);
  const [selectedItems, setSelectedItems]             = React.useState([]);
  const [selectedRegions, setSelectedRegions]         = React.useState([]);
  const [tradeStoreHighlight, setTradeStoreHighlight] = React.useState();

  const title = React.useMemo(() => {
    return whereBuy.cities.find((city) => city.urlKey === match.params.urlKey)?.title ?? '';
  }, [match.params.urlKey, whereBuy.cities]);

  const changeRegion             = React.useCallback((index) => {
    const region = whereBuy.regions[index];

    setCurrentCity(void 0);
    setCurrentRegion(index);
    setSelectedCities(region ? whereBuy.cities.filter((city) => city.regionId === region.regionId) : whereBuy.cities);
    setSelectedItems(region ? whereBuy.items.filter((item) => item.regionId === region.regionId) : whereBuy.items);
  }, [whereBuy.cities, whereBuy.items, whereBuy.regions]);
  const changeCity               = React.useCallback((index) => {
    const city = whereBuy.cities.find((city) => city.cityId === selectedCities[index]?.cityId) || whereBuy.cities[index];

    if (city) {
      catalog.getCatalogItems(`/${match.params.page}/${city.urlKey}`);
    }

    setSelectedCities(city ? whereBuy.cities.filter((cityItem) => cityItem.regionId === city.regionId) : whereBuy.cities);
    setSelectedItems(city ? whereBuy.items.filter((item) => item.cityId === city.cityId) : whereBuy.items);
    setCurrentCity(whereBuy.cities
      .filter((cityItem) => cityItem.regionId === city?.regionId)
      .findIndex((cityItem) => cityItem.cityId === city?.cityId),
    );
    setCurrentRegion(whereBuy.regions.findIndex((region) => region.regionId === city?.regionId));
  }, [match.params.page, selectedCities, whereBuy.cities, whereBuy.items, whereBuy.regions]);
  const getRegionLabel           = React.useCallback((regionId) => {
    return whereBuy.regions.find((region) => region.regionId === regionId)?.label ?? '';
  }, [whereBuy.regions]);
  const getCityLabel             = React.useCallback((cityId) => {
    const city = whereBuy.cities.find((city) => city.cityId === cityId);

    if (!city) {
      return '';
    }

    return `${t('field_cityLabel')[0].toLowerCase()}. ${city.label}, `;
  }, [whereBuy.cities]);
  const getFullAddress           = React.useCallback((regionId, cityId, address) => {
    return `${getRegionLabel(regionId)}, ${getCityLabel(cityId)}${address}`;
  }, [getCityLabel, getRegionLabel]);
  const filterAndHighlightStores = React.useCallback((stores, query) => {
    if (!query) {
      return [];
    }

    const regex = new RegExp(
      query
        .trim()
        .escapeChars(),
      'gi',
    );
    return stores
      .map((store) => {
        const fullAddress        = getFullAddress(store.regionId, store.cityId, store.address);
        const highlightedAddress = fullAddress.replace(
          regex,
          (match) => `<span class="highlight">${match}</span>`,
        );

        return {
          ...store,
          highlightedAddress,
          fullAddress,
        };
      })
      .filter((store) => store.highlightedAddress !== store.fullAddress);
  }, [getFullAddress]);
  const handleClickOutside       = React.useCallback((event) => {
    if (
      storeResultWrapperRef.current &&
      !storeResultWrapperRef.current.contains(event.target) &&
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target)
    ) {
      setIsSearchOpen(false);
    }
  }, [searchInputRef, storeResultWrapperRef]);

  React.useEffect(() => {
    whereBuy.getInitialState();

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  React.useEffect(() => {
    const city = whereBuy.cities.find((city) => city.urlKey === match.params.urlKey);

    if (whereBuy.initialStateLoaded && city) {
      setSelectedCities(whereBuy.cities.filter((currentCity) => currentCity.regionId === city.regionId));
      setSelectedItems(whereBuy.items.filter((item) => item.cityId === city.cityId));
      setSelectedRegions(whereBuy.regions);
      setCurrentCity(whereBuy.cities
        .filter((currentCity) => currentCity.regionId === city.regionId)
        .findIndex((item) => item.cityId === city.cityId),
      );
      setCurrentRegion(whereBuy.regions.findIndex((item) => item.regionId === city.regionId));
    }
  }, [whereBuy.initialStateLoaded, match.params.urlKey]);

  if (!selectedCities.length || !selectedItems.length || !selectedRegions.length) {
    return null;
  }

  return (
    <div className={'container'}>
      {whereBuy.regionData && <p className={'title'}>{title}</p>}
      <div className={'selects'}>
        {whereBuy.initialStateLoaded && (
          <>
            <Dropdown
              labelKey={'labelKey'}
              onChangeCallback={changeRegion}
              options={selectedRegions.map((region, index) => {
                return {
                  labelKey: region.label,
                  valueKey: index,
                };
              })}
              placeholder={t('client_allRegions')}
              required={false}
              selected={currentRegion}
              valueKey={'valueKey'}
            />
            <Dropdown
              labelKey={'labelKey'}
              onChangeCallback={changeCity}
              options={selectedCities.map((city, index) => {
                return {
                  labelKey: city.label,
                  valueKey: index,
                };
              })}
              placeholder={t('client_allCities')}
              required={false}
              selected={currentCity}
              valueKey={'valueKey'}
            />
            <div className={classNames('search_wrapper', { open: isSearchOpen })}>
              <input
                className={'search_input'}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setIsSearchOpen(true);
                }}
                placeholder={t('placeholder_find_salon')}
                ref={searchInputRef}
                type={'text'}
                value={searchQuery}
              />
              <LoupeIcon />
              <SearchCrossIcon
                setIsSearchOpen={setIsSearchOpen}
                setSearchQuery={setSearchQuery}
              />
              <div
                className={'store_result_wrapper'}
                ref={storeResultWrapperRef}
              >
                {filterAndHighlightStores(whereBuy.items, searchQuery).length > 0
                  ? (
                    <>
                      {filterAndHighlightStores(whereBuy.items, searchQuery)
                        .map((store) => {
                          return (
                            <p
                              className={'item'}
                              dangerouslySetInnerHTML={{ __html: store.highlightedAddress }}
                              key={store.tradeStoreId}
                              onClick={() => {
                                const cityIndex = whereBuy.cities.findIndex((city) => city.cityId === store.cityId);

                                setSearchQuery(store.fullAddress);
                                setIsSearchOpen(false);
                                changeCity(cityIndex);
                                setTradeStoreHighlight(store.tradeStoreId);
                              }}
                            />
                          );
                        })
                    }
                    </>
                  )
                  : <p className={'item_not_found'}>{t('client_salon_not_found')}</p>
                }
              </div>
            </div>
          </>
        )}
      </div>
      <div className={'content_wrapper'}>
        <div className={classNames('trade_stores_container', { grid: selectedItems.length <= 5 })}>
          {selectedItems?.map((store) => (
            <div
              className={classNames('item', { highlight: store.tradeStoreId === tradeStoreHighlight })}
              key={store.tradeStoreId}
            >
              <div className={'description'}>
                <p className={'address'}>{store.address}</p>
                <div className={'contacts_wrapper'}>
                  {store.contacts.map((contact, index) => {
                    if (contact.type === 'phone') {
                      return (
                        <div
                          className={'phone'}
                          key={index}
                        >
                          <a href={`tel:${contact.label}`}>{contact.label}</a>
                        </div>
                      );
                    }

                    if (contact.type === 'site') {
                      return (
                        <div
                          className={'site'}
                          key={index}
                        >
                          <a
                            href={contact.label}
                            target={'_blank'}
                            rel={classNames('noopener', {
                              nofollow:   contact.nofollow,
                              noindex:    contact.noindex,
                              noreferrer: contact.noreferrer,
                            })}
                          >{contact.label}</a>
                        </div>
                      );
                    }

                    if (contact.type === 'email') {
                      return (
                        <div
                          className={'site'}
                          key={index}
                        >
                          <a href={`mailto:${contact.label}`}>{contact.label}</a>
                        </div>
                      );
                    }
                  })}
                </div>
                <div
                  className={'working_time'}
                  dangerouslySetInnerHTML={{ __html: store.workTime }}
                />
                <div>
                  {store.payments.length === 1
                    ? (
                      <div className={'calculation'}>
                        {store.payments[0].label}
                      </div>
                    )
                    : (
                      <div className={'calculation'}>
                        {store.payments
                          .map((payment, index) => {
                            return index === 0
                              ? payment.label.replace(
                                ` ${t('client_calculation')
                                  .toLowerCase()}`,
                                '',
                              )
                              : payment.label.toLowerCase();
                          })
                          .join(` ${t('and')} `)}
                      </div>
                    )
                  }
                </div>
              </div>
              {DV.isString(store.image) && (
                <div
                  className={classNames('picture_container', {
                    show: DV.isArray(store.slider) && store.slider.length > 0,
                  })}
                >
                  <picture>
                    <source
                      srcSet={`${store.image}.png?w=300&default=horizontal&t=${BUILD_TIME}`}
                      type={'image/png'}
                    />
                    <source
                      srcSet={`${store.image}.webp?w=300&default=horizontal&t=${BUILD_TIME}`}
                      type={'image/webp'}
                    />
                    <img
                      alt={`${store.label} - ${store.address}`}
                      src={`${store.image}.png?w=300&default=horizontal&t=${BUILD_TIME}`}
                    />
                  </picture>
                  <div
                    className={'layer'}
                    onClick={(event) => {
                      event.currentTarget.classList.add('hidden');
                    }}
                  ><EyeIcon /></div>
                </div>
              )}
            </div>
          ))}
        </div>
        {(
          typeof currentCity !== 'undefined' &&
          selectedItems.length > 5 &&
          selectedCities[currentCity]?.urlKey === match.params.urlKey
        ) && (
          <div
            className={classNames('image_container', { link: LINK })}
            onClick={() => {
              if (LINK) {
                history.push(LINK);
              }
            }}
          >
            {IMAGE.map((image, index) => {
              return (
                <img
                  alt={ALT}
                  key={index}
                  src={addFileExtension(image)}
                  style={{
                    width:  '100%',
                    height: 'auto',
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeStores;